import React from 'react';
import PropTypes from 'prop-types';

/**
 * Enhanced TabNavigation component for navigation tabs with modern styling
 * 
 * @param {Object} props - Component props
 * @param {Array} props.tabs - Array of tab objects with id, label and optional icon
 * @param {string} props.activeTab - ID of the active tab
 * @param {function} props.onTabChange - Function to call when tab is changed
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.variant] - Tab style variant (default, pills, underline)
 * @param {boolean} [props.fullWidth] - Whether tabs should take full width
 * @returns {JSX.Element} Rendered component
 */
const TabNavigation = ({ 
  tabs, 
  activeTab, 
  onTabChange, 
  className = '',
  variant = 'default',
  fullWidth = false
}) => {
  // Style variants
  const variants = {
    default: {
      container: 'border-b border-gray-700',
      tab: (isActive) => `inline-block px-4 py-3 rounded-t-lg transition-all duration-200 ${
        isActive 
          ? 'bg-gray-800 border-b-2 border-blue-500 text-blue-400 font-medium' 
          : 'border-b-2 border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600 hover:bg-gray-800/30'
      }`
    },
    pills: {
      container: '',
      tab: (isActive) => `inline-block px-5 py-2.5 rounded-full transition-all duration-200 ${
        isActive 
          ? 'bg-blue-900/30 text-blue-400 font-medium shadow-inner' 
          : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/50'
      }`
    },
    underline: {
      container: '',
      tab: (isActive) => `inline-block px-4 py-3 transition-all duration-200 relative ${
        isActive 
          ? 'text-blue-400 font-medium' 
          : 'text-gray-400 hover:text-gray-300'
      }`
    }
  };
  
  const selectedVariant = variants[variant] || variants.default;
  
  return (
    <div className={`mb-6 ${selectedVariant.container} ${className}`}>
      <ul className={`flex text-sm font-medium text-center ${fullWidth ? 'w-full' : 'flex-wrap'}`}>
        {tabs.map(tab => (
          <li key={tab.id} className={`${fullWidth ? 'flex-1' : 'mr-2'}`}>
            <button
              className={`${selectedVariant.tab(activeTab === tab.id)} ${fullWidth ? 'w-full' : ''}`}
              onClick={() => onTabChange(tab.id)}
              aria-current={activeTab === tab.id ? 'page' : undefined}
            >
              <div className="flex items-center justify-center">
                {tab.icon && (
                  <span className="mr-2">{tab.icon}</span>
                )}
                <span>{tab.label}</span>
              </div>
              
              {/* Animated underline for underline variant */}
              {variant === 'underline' && (
                <div className={`absolute bottom-0 left-0 w-full h-0.5 transition-all duration-300 ${
                  activeTab === tab.id ? 'bg-blue-500' : 'bg-transparent'
                }`}>
                  <div className={`${
                    activeTab === tab.id 
                      ? 'w-full' 
                      : 'w-0'
                    } h-full bg-blue-500 transition-all duration-300`}></div>
                </div>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

TabNavigation.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node
    })
  ).isRequired,
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'pills', 'underline']),
  fullWidth: PropTypes.bool
};

export default TabNavigation; 